<template>
    <div
        class="md:pt-10 transition-transform"
        :class="{
            'translate-x-0': !showRedemptionPanel,
            '-translate-x-[540px]': showRedemptionPanel,
        }"
    >
        <div
            v-if="!loading && (showImageUpload || !activeTrip.image)"
            class="mb-8 relative w-100 h-[416px] rounded-lg"
        >
            <TripImageUpload
                @image-selected="handleImageSelected"
                @save="saveCoverImage"
                :query="tripDestination.valueForSearch"
                :showConfirmation="true"
                :height="400"
                :trip="activeTrip"
                :image="tripImage"
            />
            <button
                v-if="activeTrip.image"
                class="absolute top-6 right-6 border-none w-7.5 h-7.5 flex items-center justify-center text-sm cursor-pointer bg-slate-100/50 backdrop-blur px-3 py-1 rounded-full hover:bg-slate-100/75 transition-all text-black gap-2"
                @click="closeImageUpload"
            >
                <Icon icon="close" />
                <span>Cancel</span>
            </button>
            <transition-group
                tag="div"
                enter-active-class="transition ease-out duration-300"
                enter-from-class="transform opacity-0"
                enter-to-class="transform opacity-100"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100"
                leave-to-class="transform opacity-0"
            >
                <div
                    class="absolute bottom-0 left-0 right-0 flex flex-wrap justify-between pt-4 pb-2 text-white bg-gradient-to-t from-gray-500 to-transparent via-gray-500/50 rounded-lg z-10 md:flex transition-all"
                    v-if="!loading"
                >
                    <!-- Redemptions -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="track"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <p
                                class="text-xl font-bold sm:text-base md:text-lg"
                            >
                                {{ activeTrip.redemption_count }}
                            </p>
                            <p class="text-md sm:text-sm">Redemptions</p>
                        </div>
                    </div>

                    <!-- Total value -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="dollar-bill"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatCurrency(
                                                parseFloat(
                                                    activeTrip.taxes_value
                                                ) +
                                                    parseFloat(
                                                        activeTrip.cash_value
                                                    )
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover1')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover1"
                                v-click-outside="() => closePopover('popover1')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="
                                        parseFloat(
                                            activeTrip.taxes_value ?? 0
                                        ) +
                                            parseFloat(
                                                activeTrip.cash_value ?? 0
                                            ) ===
                                        0
                                    "
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div class="flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Cash cost
                                        </h3>
                                        <span
                                            class="text-[14px] text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Taxes & fees
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.taxes_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Total Value
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    parseFloat(
                                                        activeTrip.taxes_value ??
                                                            0
                                                    ) +
                                                        parseFloat(
                                                            activeTrip.cash_value ??
                                                                0
                                                        )
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Total value</p>
                        </div>
                    </div>

                    <!-- Cash spent -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="dollar-coin"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatCurrency(
                                                parseFloat(
                                                    activeTrip.cash_spend_value ??
                                                        0
                                                ) +
                                                    parseFloat(
                                                        activeTrip.cash_spend_taxes_value ??
                                                            0
                                                    )
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover2')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover2"
                                v-click-outside="() => closePopover('popover2')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="!activeTrip.cash_spend_value && !activeTrip.cash_spend_taxes_value"
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div class="flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Cash cost
                                        </h3>
                                        <span
                                            class="text-[14px] text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_spend_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Taxes & fees
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_spend_taxes_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Total Value
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    parseFloat(
                                                        activeTrip.cash_spend_value ??
                                                            0
                                                    ) +
                                                        parseFloat(
                                                            activeTrip.cash_spend_taxes_value ??
                                                                0
                                                        )
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Cash spent</p>
                        </div>
                    </div>

                    <!-- Points spent -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="points"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatNumber(
                                                activeTrip.payment_points
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover3')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover3"
                                v-click-outside="() => closePopover('popover3')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="activeTrip.payment_points === 0"
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div
                                        class="flex justify-between mb-3"
                                        v-for="program in computedPointsPrograms"
                                        :key="program.payment_program_id"
                                    >
                                        <img
                                            :src="`${cdnUrl}/${program?.payment_program?.program_image?.path}/_small/${program?.payment_program?.program_image?.file_name}`"
                                            :title="
                                                program.payment_program?.name
                                            "
                                            :alt="program.payment_program?.name"
                                            class="mw-[160px] h-[20px]"
                                        />
                                        <span
                                            class="text-[12px] text-slate-800"
                                        >
                                            {{
                                                formatNumber(
                                                    program.payment_points
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[12px] font-medium"
                                        >
                                            Total
                                        </h3>
                                        <span
                                            class="text-[12px] text-right text-slate-800"
                                        >
                                            {{
                                                formatNumber(
                                                    activeTrip.payment_points
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Points spent</p>
                        </div>
                    </div>
                </div>
            </transition-group>
        </div>
        <div
            v-else
            class="bg-no-repeat bg-center bg-cover mb-6 md:mb-8 relative w-full h-[200px] sm:h-[300px] md:h-[416px] rounded-lg"
            :style="`background-image: url(${tripImage})`"
        >
            <transition-group
                tag="div"
                enter-active-class="transition ease-out duration-300"
                enter-from-class="transform opacity-0"
                enter-to-class="transform opacity-100"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="transform opacity-100"
                leave-to-class="transform opacity-0"
            >
                <div
                    class="absolute bottom-0 left-0 right-0 flex flex-wrap justify-between pt-4 text-white bg-gradient-to-t from-gray-500 to-transparent via-gray-500/50 rounded-lg z-10 md:flex transition-all"
                    v-if="!loading"
                >
                    <!-- Redemptions -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="track"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <p
                                class="text-xl font-bold sm:text-base md:text-lg"
                            >
                                {{ activeTrip.redemption_count }}
                            </p>
                            <p class="text-md sm:text-sm">Redemptions</p>
                        </div>
                    </div>

                    <!-- Total value -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="dollar-bill"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatCurrency(
                                                parseFloat(
                                                    activeTrip.taxes_value ?? 0
                                                ) +
                                                    parseFloat(
                                                        activeTrip.cash_value ??
                                                            0
                                                    )
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover1')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover1"
                                v-click-outside="() => closePopover('popover1')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="
                                        parseFloat(
                                            activeTrip.taxes_value ?? 0
                                        ) +
                                            parseFloat(
                                                activeTrip.cash_value ?? 0
                                            ) ===
                                        0
                                    "
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div class="flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Cash cost
                                        </h3>
                                        <span
                                            class="text-[14px] text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Taxes & fees
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.taxes_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Total Value
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    parseFloat(
                                                        activeTrip.taxes_value ??
                                                            0
                                                    ) +
                                                        parseFloat(
                                                            activeTrip.cash_value ??
                                                                0
                                                        )
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Total value</p>
                        </div>
                    </div>

                    <!-- Cash spent -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="dollar-coin"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatCurrency(
                                                parseFloat(
                                                    activeTrip.cash_spend_value ??
                                                        0
                                                ) +
                                                    parseFloat(
                                                        activeTrip.cash_spend_taxes_value ??
                                                            0
                                                    )
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover2')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover2"
                                v-click-outside="() => closePopover('popover2')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="!activeTrip.cash_spend_value && !activeTrip.cash_spend_taxes_value"
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div class="flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Cash cost
                                        </h3>
                                        <span
                                            class="text-[14px] text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_spend_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Taxes & fees
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    activeTrip.cash_spend_taxes_value
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[14px] font-medium"
                                        >
                                            Total Value
                                        </h3>
                                        <span
                                            class="text-[14px] text-right text-slate-800"
                                        >
                                            {{
                                                formatCurrency(
                                                    parseFloat(
                                                        activeTrip.cash_spend_value ??
                                                            0
                                                    ) +
                                                        parseFloat(
                                                            activeTrip.cash_spend_taxes_value ??
                                                                0
                                                        )
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Cash spent</p>
                        </div>
                    </div>

                    <!-- Points spent -->
                    <div
                        class="hidden md:flex items-center pt-8 px-4 pb-4 sm:px-2 sm:py-2 rounded-lg w-full sm:w-1/2 md:w-1/4 min-w-[180px]"
                    >
                        <div
                            class="bg-gray-600 bg-opacity-75 p-4 rounded-full flex items-center justify-center w-12 h-12"
                        >
                            <Icon
                                icon="points"
                                class="text-white"
                                height="24"
                                width="24"
                            />
                        </div>
                        <div class="ml-4">
                            <div class="flex items-center">
                                <p
                                    class="text-xl font-bold sm:text-base md:text-lg text-left"
                                >
                                    <span
                                        class="font-bold sm:text-base md:text-lg"
                                    >
                                        {{
                                            formatNumber(
                                                activeTrip.payment_points
                                            )
                                        }}
                                    </span>
                                </p>
                                <icon
                                    icon="chevron-down"
                                    :width="16"
                                    :height="16"
                                    class="text-white cursor-pointer icon-down ml-2"
                                    @click="togglePopover('popover3')"
                                ></icon>
                            </div>
                            <!-- Popover -->
                            <div
                                v-if="isPopoverOpen.popover3"
                                v-click-outside="() => closePopover('popover3')"
                                class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg w-60 p-4 flex-col items-start gap-3"
                            >
                                <template
                                    v-if="activeTrip.payment_points === 0"
                                >
                                    <p
                                        class="text-slate-500 text-[14px] font-medium"
                                    >
                                        Details will display once you begin to
                                        add redemptions to this trip.
                                    </p>
                                </template>
                                <template v-else>
                                    <div
                                        class="flex justify-between mb-3"
                                        v-for="program in computedPointsPrograms"
                                        :key="program.payment_program_id"
                                    >
                                        <img
                                            :src="`${cdnUrl}/${program?.payment_program?.program_image?.path}/_small/${program?.payment_program?.program_image?.file_name}`"
                                            :title="
                                                program.payment_program?.name
                                            "
                                            :alt="program.payment_program?.name"
                                            class="mw-[160px] h-[20px]"
                                        />
                                        <span
                                            class="text-[12px] text-slate-800"
                                        >
                                            {{
                                                formatNumber(
                                                    program.payment_points
                                                )
                                            }}
                                        </span>
                                    </div>
                                    <div
                                        class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"
                                    ></div>
                                    <div class="mt-4 flex justify-between">
                                        <h3
                                            class="text-slate-500 text-[12px] font-medium"
                                        >
                                            Total
                                        </h3>
                                        <span
                                            class="text-[12px] text-right text-slate-800"
                                        >
                                            {{
                                                formatNumber(
                                                    activeTrip.payment_points
                                                )
                                            }}
                                        </span>
                                    </div>
                                </template>
                            </div>
                            <p class="text-md sm:text-sm">Points spent</p>
                        </div>
                    </div>
                </div>
            </transition-group>
            <!-- Statistics section end -->

            <!-- Buttons (edit/save) -->
            <button
                class="absolute bottom-4 right-4 w-10 h-10 rounded bg-white z-20"
                @click="showOptions = !showOptions"
                v-click-outside="() => (showOptions = false)"
            >
                <Icon icon="edit" />
            </button>
            <button
                class="absolute bottom-4 right-16 w-10 h-10 rounded bg-white z-20"
                v-if="isUpdating"
                @click="saveCoverImage"
                title="Save changes"
            >
                <icon icon="check" :height="16" :width="16" />
            </button>

            <!-- Options -->
            <transition name="fade">
                <div class="absolute top-full right-5 z-10" v-if="showOptions">
                    <div class="bg-white p-3 rounded-lg shadow">
                        <ul class="text-sm">
                            <li>
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="regenerateCover"
                                >
                                    <Icon icon="rotate" />
                                    <span>Regenerate cover</span>
                                </button>
                            </li>
                            <li>
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="openImageUpload"
                                >
                                    <Icon
                                        icon="upload"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Upload</span>
                                </button>
                            </li>
                            <li v-if="hasTripImage">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="showDeleteTripCoverConfirm = true"
                                >
                                    <Icon
                                        icon="trash"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Remove</span>
                                </button>
                            </li>
                            <li v-if="isUpdating">
                                <button
                                    class="px-2 py-1 text-left flex items-center gap-2"
                                    @click="cancelCoverChanges"
                                >
                                    <Icon
                                        icon="reset"
                                        :width="16"
                                        :height="16"
                                    />
                                    <span>Cancel</span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
            </transition>
        </div>
        <div
            class="w-full flex flex-col md:flex-row justify-between items-start md:items-center mb-4"
        >
            <div class="w-full md:w-auto mb-4 md:mb-0">
                <div
                    class="flex flex-col md:flex-row items-start md:items-center mb-2"
                >
                    <h2 class="text-slate-800 text-2xl font-semibold">
                        {{ activeTrip?.title }}
                    </h2>
                    <p
                        v-if="activeTrip?.is_shared"
                        @click="openShareModal"
                        class="bg-orange-100 hover:bg-orange-50 text-orange p-2 px-2 rounded-lg flex items-center text-xs font-medium leading-none ml-0 md:ml-2 h-6 gap-1 cursor-pointer mt-2 md:mt-0"
                    >
                        <Icon
                            icon="globe"
                            v-bind:width="16"
                            v-bind:height="16"
                            pathClass="text-orange"
                        />
                        Shared
                    </p>
                </div>
                <p class="text-slate-500 text-sm mb-1 flex items-center">
                    {{ dayjs(activeTrip?.start_date).format('MMM D') }} &ndash;
                    {{ dayjs(activeTrip?.end_date).format('MMM D, YYYY') }}
                    <span class="px-2 text-slate-500">
                        <div
                            class="w-0.5 h-0.5 bg-slate-500 rounded-full"
                        ></div>
                    </span>
                    <span v-if="activeTrip?.city || activeTrip?.state">
                        {{
                            activeTrip?.city
                                ? activeTrip?.city
                                : activeTrip?.state
                        }},&nbsp;
                    </span>
                    {{ activeTrip?.country }}
                </p>
                <p class="text-slate-800 text-sm mb-1">
                    {{ activeTrip?.description }}
                </p>
            </div>
            <div class="flex flex-row gap-2 items-center">
                <a
                    href="#"
                    @click.prevent="setShowAffiliateModal(true)"
                    class="text-orange text-xs hover:text-orange-400 whitespace-nowrap mr-2"
                >
                    Affiliate Disclosure
                </a>
                <button
                    type="button"
                    class="btn btn-orange min-h-9 !flex !p-[8px_16px] !justify-center !items-center text-xs sm:text-base"
                    @click="tripsStore.setShowRedemptionCreateModal(true)"
                >
                    <Icon icon="plus" />
                    <span class="ml-2 whitespace-nowrap">Add Redemption</span>
                </button>
                <button
                    type="button"
                    class="btn hover:bg-slate-100 transition-colors bg-white shadow-sm border border-slate-200 gap-1.5 min-h-10 !p-2 sm:!p-[8px_16px] !flex !items-center !justify-center"
                    @click="openShareModal"
                >
                    <Icon
                        v-if="activeTrip?.is_shared"
                        icon="globe-dashed"
                        v-bind:width="16"
                        v-bind:height="16"
                    />
                    <Icon
                        v-else
                        icon="globe"
                        v-bind:width="16"
                        v-bind:height="16"
                    />
                    <span class="hidden md:inline">
                        {{ activeTrip?.is_shared ? 'Unshare' : 'Share' }}
                    </span>
                </button>
                <TripMoreActionsBtn
                    :show-label="false"
                    :show-share="false"
                    :trip="activeTrip"
                    class="h-10"
                />
            </div>
        </div>
        <div
            class="flex justify-between items-center mb-2 sm:mb-0 mt-10 sm:mt-0"
        >
            <h3 class="text-slate-800 text-lg font-medium">Redemptions</h3>
        </div>

        <!-- Statistics section for mobile -->
        <div
            class="grid grid-cols-2 gap-2 text-black rounded-lg z-10 md:hidden"
        >
            <div class="flex items-center pl-0 p-2 gap-2">
                <!-- icon -->
                <div class="flex items-center justify-start h-10">
                    <Icon
                        icon="track"
                        class="text-gray-500"
                        height="20"
                        width="20"
                        containerClass="pl-0"
                    />
                </div>
                <div class="ml-2">
                    <p class="text-lg font-medium">
                        {{ activeTrip.redemption_count }}
                    </p>
                    <p class="text-sm text-gray-500">Redemptions</p>
                </div>
            </div>

            <div class="flex items-center pl-0 p-2 gap-2">
                <!-- icon -->
                <div class="flex items-center justify-start h-10">
                    <Icon
                        icon="dollar-bill"
                        class="text-gray-500"
                        height="20"
                        width="20"
                    />
                </div>
                <div class="ml-2">
                    <div class="flex items-center">
                        <p class="text-lg font-medium">
                            {{
                                formatCurrency(
                                    parseFloat(activeTrip.taxes_value) +
                                        parseFloat(activeTrip.cash_value)
                                )
                            }}
                        </p>
                        <icon
                            icon="chevron-down"
                            :width="14"
                            :height="14"
                            class="text-black cursor-pointer icon-down ml-1"
                            @click="togglePopover('popover1')"
                        ></icon>
                    </div>
                    <!-- Content Popover -->
                    <div
                        v-if="isPopoverOpen.popover1"
                        v-click-outside="() => closePopover('popover1')"
                        class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg w-60 p-4 flex-col items-start gap-3"
                    >
                        <template
                            v-if="
                                parseFloat(activeTrip.taxes_value) +
                                    parseFloat(activeTrip.cash_value) ===
                                0
                            "
                        >
                            <p class="text-slate-500 text-[12px] font-medium">
                                Details will display once you begin to add
                                redemptions to this trip.
                            </p>
                        </template>
                        <template v-else>
                            <div class="flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Cash cost
                                </h3>
                                <span class="text-[12px] text-slate-800">
                                    {{ formatCurrency(activeTrip.cash_value) }}
                                </span>
                            </div>
                            <div class="mt-4 flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Taxes & fees
                                </h3>
                                <span
                                    class="text-[12px] text-right text-slate-800"
                                >
                                    {{ formatCurrency(activeTrip.taxes_value) }}
                                </span>
                            </div>
                            <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                            <div class="mt-4 flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Total Value
                                </h3>
                                <span
                                    class="text-[12px] text-right text-slate-800"
                                >
                                    {{
                                        formatCurrency(
                                            parseFloat(activeTrip.taxes_value) +
                                                parseFloat(
                                                    activeTrip.cash_value
                                                )
                                        )
                                    }}
                                </span>
                            </div>
                        </template>
                    </div>
                    <p class="text-sm text-gray-500">Total value</p>
                </div>
            </div>

            <div class="flex items-center pl-0 p-2 gap-2">
                <!-- icon -->
                <div class="flex items-center justify-start h-10">
                    <Icon
                        icon="dollar-coin"
                        class="text-gray-500"
                        height="20"
                        width="20"
                    />
                </div>
                <div class="ml-2">
                    <div class="flex items-center">
                        <p class="text-lg font-medium">
                            {{
                                formatCurrency(
                                    parseFloat(activeTrip.cash_spend_value) +
                                        parseFloat(
                                            activeTrip.cash_spend_taxes_value
                                        )
                                )
                            }}
                        </p>
                        <icon
                            icon="chevron-down"
                            :width="14"
                            :height="14"
                            class="text-black cursor-pointer icon-down ml-1"
                            @click="togglePopover('popover2')"
                        />
                    </div>
                    <!-- Content Popover -->
                    <div
                        v-if="isPopoverOpen.popover2"
                        v-click-outside="() => closePopover('popover2')"
                        class="absolute left-auto right-30 md:right-[5px] md:left-auto z-10 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg w-40 md:w-60 p-2 md:p-4 flex-col items-start gap-2 md:gap-3"
                    >
                        <template
                            v-if="!activeTrip.cash_spend_value && !activeTrip.cash_spend_taxes_value"
                        >
                            <p class="text-slate-500 text-[12px] font-medium">
                                Details will display once you begin to add
                                redemptions to this trip.
                            </p>
                        </template>
                        <template v-else>
                            <div class="flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Cash cost
                                </h3>
                                <span class="text-[12px] text-slate-800">
                                    {{
                                        formatCurrency(
                                            activeTrip.cash_spend_value
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="mt-4 flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Taxes & fees
                                </h3>
                                <span
                                    class="text-[12px] text-right text-slate-800"
                                >
                                    {{
                                        formatCurrency(
                                            activeTrip.cash_spend_taxes_value
                                        )
                                    }}
                                </span>
                            </div>
                            <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                            <div class="mt-4 flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Total Value
                                </h3>
                                <span
                                    class="text-[12px] text-right text-slate-800"
                                >
                                    {{
                                        formatCurrency(
                                            parseFloat(
                                                activeTrip.cash_spend_value
                                            ) +
                                                parseFloat(
                                                    activeTrip.cash_spend_taxes_value
                                                )
                                        )
                                    }}
                                </span>
                            </div>
                        </template>
                    </div>
                    <p class="text-sm whitespace-nowrap text-gray-500">
                        Cash spent
                    </p>
                </div>
            </div>

            <div class="flex items-center pl-0 p-2 gap-2">
                <!-- icon -->
                <div class="flex items-center justify-start h-10">
                    <Icon
                        icon="points"
                        class="text-gray-500"
                        height="20"
                        width="20"
                    />
                </div>
                <div class="ml-2">
                    <div class="flex items-center">
                        <p class="text-lg font-medium">
                            {{ formatNumber(activeTrip.payment_points) }}
                        </p>
                        <icon
                            icon="chevron-down"
                            :width="14"
                            :height="14"
                            class="text-black cursor-pointer icon-down ml-1"
                            @click="togglePopover('popover3')"
                        ></icon>
                    </div>
                    <!-- Content Popover -->
                    <div
                        v-if="isPopoverOpen.popover3"
                        v-click-outside="() => closePopover('popover3')"
                        class="absolute transform -translate-x-[60%] z-10 mt-2 bg-white border border-gray-200 rounded-lg w-60 p-4 flex-col items-start gap-3"
                    >
                        <template v-if="activeTrip.payment_points === 0">
                            <p class="text-slate-500 text-[12px] font-medium">
                                Details will display once you begin to add
                                redemptions to this trip.
                            </p>
                        </template>
                        <template v-else>
                            <div
                                class="flex justify-between mb-3"
                                v-for="program in activeTrip.payment_points_detail"
                                :key="program.payment_program_id"
                            >
                                <ImageLoader>
                                    <template #image>
                                        <img
                                            :src="
                                                program.payment_program.image.replace(
                                                    'storage',
                                                    'images'
                                                )
                                            "
                                            :title="
                                                program.payment_program?.name
                                            "
                                            class="mw-[160px] h-[20px]"
                                        />
                                    </template>
                                    <template #error>
                                        <Icon
                                            icon="points"
                                            class="text-slate-600 w-8 h-5"
                                        />
                                        <span>
                                            {{ program.payment_program?.name }}
                                        </span>
                                    </template>
                                </ImageLoader>
                                <span class="text-[12px] text-slate-800">
                                    {{ formatNumber(program.payment_points) }}
                                </span>
                            </div>
                            <div class="mt-2 w-52 h-0.5 bg-[#E2E8F0]"></div>
                            <div class="mt-4 flex justify-between">
                                <h3
                                    class="text-slate-500 text-[12px] font-medium"
                                >
                                    Total
                                </h3>
                                <span
                                    class="text-[12px] text-right text-slate-800"
                                >
                                    {{
                                        formatNumber(activeTrip.payment_points)
                                    }}
                                </span>
                            </div>
                        </template>
                    </div>
                    <p class="text-sm whitespace-nowrap text-gray-500">
                        Points spent
                    </p>
                </div>
            </div>
        </div>

        <div class="flex-col gap-5 relative mt-4 sm:mt-0">
            <div
                class="w-0 h-[98%] absolute top-0 left-[30px] border border-slate-300 -z-10"
                v-if="redemptions.length > 0"
            ></div>
            <RedemptionDateSegment
                v-for="dateSegment in redemptions"
                :date-segment="dateSegment"
                :key="dateSegment"
            />
        </div>

        <EmptyState
            v-if="redemptions.length === 0 && showEmptyRedemptionsState"
            class="mb-10"
            :icon="'squiggle'"
            title="No Redemptions yet"
            description="Add your first redemption to plan your trip and track points usage."
        >
            <template #action>
                <button
                    class="btn btn-secondary"
                    @click="tripsStore.setShowRedemptionCreateModal(true)"
                >
                    &plus; Add Redemption
                </button>
            </template>
        </EmptyState>

        <Teleport to="body">
            <CreateRedemption v-if="showRedemptionCreateModal" />
            <EditRedemption v-if="showRedemptionEditModal" />

            <ShareTripModal
                :show="showShareModal"
                :trip="activeTrip"
                :shareToken="shareToken"
                :loading="loading"
                @close="closeShareModal"
            />

            <ConfirmDialag
                :show="showDeleteTripConfirm"
                title="Delete trip"
                dialog="You are about to delete the trip. This action cannot be undone."
                icon="trash"
                iconColor="red"
                :buttons="[
                    {
                        type: 'cancel',
                        text: 'Cancel',
                    },
                    {
                        type: 'confirm',
                        text: 'Delete trip',
                        value: true,
                        classes: '!bg-red-500 !text-white',
                    },
                ]"
            />

            <ConfirmDialag
                :show="showDeleteRedemptionConfirm"
                title="Delete redemption"
                dialog="You are about to delete this redemption. This action cannot be undone."
                @cancel="tripsStore.setShowDeleteRedemptionConfirm(false)"
                @confirm="tripsStore.deleteRedemption(activeRedemption.uuid)"
                :buttons="[
                    {
                        type: 'cancel',
                        text: 'Cancel',
                    },
                    {
                        type: 'confirm',
                        text: 'Delete redemption',
                        value: true,
                    },
                ]"
            />

            <ConfirmDialag
                :show="showDeleteTripConfirm"
                title="Delete trip"
                dialog="You are about to delete the trip. This action cannot be undone."
                :buttons="[
                    {
                        type: 'cancel',
                        text: 'Cancel',
                    },
                    {
                        type: 'confirm',
                        text: 'Delete trip',
                        value: true,
                    },
                ]"
            />
            <ConfirmDialag
                :show="showDeleteTripCoverConfirm"
                title="Delete trip cover image"
                dialog="You are about to delete the trip cover image. This action cannot be undone."
                icon="trash"
                iconColor="red"
                :buttons="[
                    {
                        type: 'cancel',
                        text: 'Cancel',
                    },
                    {
                        type: 'confirm',
                        text: 'Delete Image',
                        value: true,
                        classes: '!bg-red-500 !text-white',
                    },
                ]"
                @cancel="showDeleteTripCoverConfirm = false"
                @confirm="clearImage"
            />

            <ManageTrip />
            <RedemptionPanel v-if="showRedemptionPanel" />
        </Teleport>
        <LoadingState :loading="loading" />
    </div>
</template>

<script setup>
import { useRoute } from 'vue-router';
import { ref, onBeforeMount, onMounted, computed, watch } from 'vue';
import dayjs from 'dayjs';
import { notify } from 'notiwind';
import currency from 'currency.js';

import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import { useAppStore } from '@/store/app';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import CreateRedemption from '@/components/trips/modals/create-redemption.vue';
import EditRedemption from '@/components/trips/modals/edit-redemption.vue';
import ShareTripModal from '@/components/trips/modals/share-trip-modal.vue';
import RedemptionDateSegment from '@/components/trips/redemptions/redemption-date-segment.vue';
import Icon from '@/components/ui/icon/icon.vue';
import ImageLoader from '@/components/ui/image-loader/image-loader.vue';
import ConfirmDialag from '@/components/ui/confirmation-dialog/confirmation-dialog.vue';
import EmptyState from '@/components/ui/empty-state/empty-state.vue';
import ManageTrip from '@/components/trips/modals/manage-trip.vue';
import TripMoreActionsBtn from './trip-more-actions-btn.vue';
import RedemptionPanel from '@/components/trips/redemptions/redemption-panel.vue';
import TripImageUpload from '@/components/trips/trip-image-upload.vue';

const route = useRoute();
const tripsStore = useTripsStore();
const appStore = useAppStore();
const showImageUpload = ref(false);
const showShareModal = ref(false);
const isUpdating = ref(false);
const originalImageCover = ref(null);

const {
    activeTrip,
    loading,
    redemptions,
    showRedemptionCreateModal,
    showRedemptionEditModal,
    showEmptyRedemptionsState,
    showRedemptionPanel,
    activeRedemption,
    showDeleteRedemptionConfirm,
    upcomingTrips,
    pastTrips,
} = storeToRefs(tripsStore);
// const trip = computed(() => tripsStore.activeTrip);

const shareToken = computed(() => {
    return activeTrip.value?.share_token;
});
const hasTripImage = computed(() => {
    return activeTrip.value?.image?.path;
});

const formatCurrency = (amount) => {
    if (amount == null) {
        return '$0.00';
    }
    return currency(amount, { symbol: '$', precision: 2 }).format();
};

const formatNumber = (number) => {
    return Intl.NumberFormat('en-US').format(number);
};

const cdnUrl = computed(() => {
    return window.cdnUrl;
});

const tripImage = computed(() => {
    if (!activeTrip.value.image) return null;
    if (
        typeof activeTrip.value.image === 'string' &&
        activeTrip.value.image.startsWith('http')
    ) {
        return activeTrip.value.image;
    }
    return `${cdnUrl.value}/${activeTrip.value.image.path}/${activeTrip.value.image.file_name}`;
});

const computedPointsPrograms = computed(() => {
    return activeTrip.value?.payment_points_detail?.reduce(
        (arr, redemption) => {
            const programIndex = arr.findIndex(
                (item) =>
                    item.payment_program_id == redemption.payment_program_id
            );
            if (programIndex === -1) {
                arr.push({
                    payment_program: redemption.payment_program,
                    payment_points: redemption.payment_points,
                    payment_program_id: redemption.payment_program_id,
                });
            } else {
                arr[programIndex].payment_points += redemption.payment_points;
            }

            return arr;
        },
        []
    );
});

const isPopoverOpen = ref({
    popover1: false,
    popover2: false,
    popover3: false,
});

const togglePopover = (popover) => {
    Object.keys(isPopoverOpen.value).forEach((key) => {
        isPopoverOpen.value[key] = false;
    });
    isPopoverOpen.value[popover] = !isPopoverOpen.value[popover];
};

const closePopover = (popover) => {
    isPopoverOpen.value[popover] = false;
};

const openShareModal = () => {
    showShareModal.value = true;
};

const closeShareModal = () => {
    showShareModal.value = false;
};

const showDeleteTripConfirm = ref(false);
const showDeleteTripCoverConfirm = ref(false);

onBeforeMount(() => {
    const tripUuid = route.params.tripUuid;
    useTripsStore().getTrip(tripUuid);
    useTripsStore().getRedemptions(tripUuid);
});

onMounted(() => {
    if (!upcomingTrips.length && !pastTrips.length) useTripsStore().getTrips();
});

const showOptions = ref(false);

const regenerateCover = async () => {
    isUpdating.value = true;
    originalImageCover.value = activeTrip.value.image.path;
    try {
        const response = await ApiService.apiCall(
            `/media/random-image/${tripDestination.value.valueForSearch}`,
            'get'
        );
        activeTrip.value = {
            ...activeTrip.value,
            image: response.data.images.regular,
        };
    } catch (error) {
        notify({
            group: 'error',
            title: 'Error',
            text: 'Failed to regenerate cover image',
        });
    }
};

const clearImage = () => {
    showImageUpload.value = true;
    activeTrip.value.image = null;
    tripsStore.updateTrip({
        message: 'Trip cover image removed',
    });
    showDeleteTripCoverConfirm.value = false;
};

const closeImageUpload = () => {
    showImageUpload.value = false;
};

const openImageUpload = () => {
    showImageUpload.value = true;
};

const cancelCoverChanges = () => {
    isUpdating.value = false;
    showImageUpload.value = false;
    activeTrip.value.image.path = originalImageCover.value;
    originalImageCover.value = null;
};

const handleImageSelected = async (imagePath) => {
    if (!activeTrip.value.id) {
        notify({
            group: 'error',
            title: 'Update Failed',
            text: 'Trip ID is missing. Cannot update the trip.',
        });
        return;
    }

    showImageUpload.value = true;
    activeTrip.value = {
        ...activeTrip.value,
        image: imagePath,
    };
};

const saveCoverImage = async () => {
    try {
        await tripsStore.updateTrip({
            message: 'Trip cover image updated',
            ...activeTrip.value,
            image: activeTrip.value.image,
        });
        showImageUpload.value = false;
    } catch (error) {
        notify({
            group: 'error',
            title: 'Update Failed',
            text: 'Failed to update the trip with the new cover image.',
        });
        cancelCoverChanges();
    }

    isUpdating.value = false;
    showImageUpload.value = false;
};

const tripDestination = computed({
    get() {
        const city = activeTrip.value.city || '';
        let state = activeTrip.value.state || '';
        if (state) {
            state = ' ' + state;
        }
        const country = activeTrip.value.country;
        return {
            formattedAddress:
                (state || city) && country
                    ? `${city}${state}, ${country}`
                    : null,
            city,
            state,
            country,
            valueForSearch: `${city.trim()}+${state.trim()}+${country?.trim()}`
                .replace(/,/g, '')
                .replace(/ /g, '+')
                .replace(/^\+/, '')
                .replace(/\+$/, '')
                .replace(/\+\+/, '+')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, ''),
        };
    },
    set(value) {
        tripsStore.setActiveTrip({ ...activeTrip.value, destination: value });
    },
});

const { setShowAffiliateModal } = appStore;
</script>
